import { h, Component, Fragment } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

// One-time Vipps QR for customer-present payment in the clinic: the QR is shown on the clinic's
// own screen and the visitor scans it with the Vipps app. Opt-in through userFlow 'QR' on
// /api/vipps/request-payment; the backend returns the QR image URL as redirectUrl.
// The payment is captured by the status poll (get-payment-status.js), which also marks the sale.
const POLL_INTERVAL_MS = 3000;
// A Vipps one-time QR lives for 10 minutes, so stop polling when it has expired.
const POLL_MAX_ATTEMPTS = 200;

@observer
class SalePaymentVippsQr extends Component {
    constructor(props) {
        super(props);
        this.state = {
            status: 'creating',
            qrUrl: null,
            paymentUuidv4: null,
            errorId: null,
        };
        this.unmounted = false;
    }

    componentDidMount() {
        this.createPayment();
    }

    componentWillUnmount() {
        this.unmounted = true;
        this.cancelPending();
    }

    // Void a QR the visitor never scanned so it cannot be paid after the drawer is closed.
    cancelPending = () => {
        const { vippsStore } = this.props.stores;
        const { status, paymentUuidv4 } = this.state;
        if (status === 'waiting' && paymentUuidv4) {
            vippsStore.paymentCancel(paymentUuidv4);
        }
    }

    isCurrent = paymentUuidv4 => !this.unmounted
        && this.state.status === 'waiting'
        && this.state.paymentUuidv4 === paymentUuidv4;

    createPayment = async () => {
        const { amount, title, customer, sale } = this.props;
        const { vippsStore } = this.props.stores;
        if (!amount || amount <= 0) {
            return;
        }
        this.cancelPending();
        this.setState({
            status: 'creating', qrUrl: null, paymentUuidv4: null, errorId: null,
        });

        const response = await vippsStore.requestPayment({
            amount,
            title,
            customer,
            sale,
            userFlow: 'QR',
        });
        const paymentUuidv4 = response?.data?.paymentUuidv4;
        const qrUrl = response?.data?.redirectUrl;

        if (this.unmounted) {
            if (paymentUuidv4) {
                vippsStore.paymentCancel(paymentUuidv4);
            }
            return;
        }
        if (response?.status !== 200 || !qrUrl || !paymentUuidv4) {
            this.setState({ status: 'failed', errorId: 'create-failed' });
            return;
        }

        this.setState({ status: 'waiting', qrUrl, paymentUuidv4 });
        vippsStore.pollPaymentStatus(paymentUuidv4, {
            onComplete: data => this.handleComplete(paymentUuidv4, data),
            onFailed: data => this.handleFailed(paymentUuidv4, data),
            maxAttempts: POLL_MAX_ATTEMPTS,
            interval: POLL_INTERVAL_MS,
        });
    }

    handleComplete = async (paymentUuidv4, data = {}) => {
        if (!this.isCurrent(paymentUuidv4)) {
            return;
        }
        this.setState({ status: 'paid' });
        const { callbackPaymentDone = () => {}, amount } = this.props;
        await callbackPaymentDone({
            paymentUuidv4,
            paymentStatus: data.paymentStatus,
            amount,
            currency: data.currency || 'NOK',
        });
    }

    handleFailed = (paymentUuidv4, data = {}) => {
        if (!this.isCurrent(paymentUuidv4)) {
            return;
        }
        const { vippsStore } = this.props.stores;
        if (data.timeout) {
            // Polling gave up before Vipps reported a final state: void the QR ourselves.
            vippsStore.paymentCancel(paymentUuidv4);
        }
        this.setState({ status: 'failed', errorId: data.timeout ? 'timeout' : 'expired' });
    }

    render() {
        const { amount } = this.props;
        const { status, qrUrl, errorId } = this.state;

        return (
            <div class='d-flex flex-column align-items-center text-center py-2'>
                {status === 'creating' && <>
                    <div class='display-4 mb-2'>
                        <i class='fa-duotone fa-spinner-third fa-spin' />
                    </div>
                    <p class='text-body-secondary mb-0'>
                        <Text id='dyrejournal.vipps-qr-creating'>Creating QR code...</Text>
                    </p>
                </>}

                {status === 'waiting' && <>
                    <p class='fs-5 mb-2'>
                        <Text id='dyrejournal.vipps-qr-instruction'>Ask the customer to scan the QR code with the Vipps app.</Text>
                    </p>
                    <img
                        src={qrUrl}
                        alt='Vipps QR'
                        class='bg-white rounded p-2 mb-2'
                        style='width: 280px; height: 280px;'
                    />
                    <p class='fs-4 fw-bold mb-1'>
                        <Text id='dyrejournal.vipps-qr-amount' fields={{ amount: util.format(amount, 2, ',', ' ') }}>
                            NOK {util.format(amount, 2, ',', ' ')}
                        </Text>
                    </p>
                    <p class='text-body-secondary mb-0'>
                        <i class='fa-duotone fa-spinner-third fa-spin me-2' />
                        <Text id='dyrejournal.vipps-qr-waiting'>Waiting for the customer to approve the payment...</Text>
                    </p>
                </>}

                {status === 'paid' && <>
                    <div class='display-4 text-success mb-2'>
                        <i class='fa-duotone fa-circle-check' />
                    </div>
                    <p class='fs-5 mb-0'>
                        <Text id='dyrejournal.vipps-qr-paid'>The payment is approved.</Text>
                    </p>
                </>}

                {status === 'failed' && <>
                    <div class='alert alert-warning w-100' role='alert'>
                        {errorId === 'create-failed' && <Text id='dyrejournal.vipps-qr-create-failed'>Could not create the Vipps payment. Try again.</Text>}
                        {errorId === 'timeout' && <Text id='dyrejournal.vipps-qr-timeout'>The QR code has expired.</Text>}
                        {errorId === 'expired' && <Text id='dyrejournal.vipps-qr-expired'>The QR code has expired or the payment was cancelled.</Text>}
                    </div>
                    <button type='button' class='btn btn-orange rounded-pill' onClick={this.createPayment}>
                        <i class='fa-duotone fa-qrcode me-2' />
                        <Text id='dyrejournal.vipps-qr-new'>Create new QR code</Text>
                    </button>
                </>}
            </div>
        );
    }
}

export default SalePaymentVippsQr;
